import { useEffect, useRef, useState, memo, useCallback } from "react";
import { FiMapPin, FiCrosshair } from "react-icons/fi";

const API_BASE = "https://track-map.gwarm.net/api/areas";

interface OutletGoogleMapProps {
  onLocationPicked: (data: {
    lat: number;
    lng: number;
    address: string;
    shortAddress: string;
    countryName: string;
    cityName: string;
    districtName: string;
    street: string;
    buildingNumber: string;
    postalCode: string;
    source: string;
  }) => void;
  initialLat?: number | string;
  initialLng?: number | string;
  /** External pin position — when parent updates (e.g. from short address input), map pans here */
  pinPosition?: { lat: number; lng: number } | null;
  /** Snapped position from DB — map smoothly pans to this */
  snappedPosition?: { lat: number; lng: number } | null;
}

declare global {
  interface Window {
    google?: any;
  }
}

const OutletGoogleMap = ({
  onLocationPicked,
  initialLat,
  initialLng,
  pinPosition,
  snappedPosition,
}: OutletGoogleMapProps) => {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);

  const [mapReady, setMapReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const onLocationPickedRef = useRef(onLocationPicked);

  useEffect(() => {
    onLocationPickedRef.current = onLocationPicked;
  }, [onLocationPicked]);

  // ── Move marker + pan map when pinPosition changes externally ──
  useEffect(() => {
    if (!mapReady || !pinPosition) return;
    const { lat, lng } = pinPosition;
    if (markerRef.current) {
      markerRef.current.setPosition({ lat, lng });
    }
    if (mapInstanceRef.current) {
      mapInstanceRef.current.panTo({ lat, lng });
      mapInstanceRef.current.setZoom(16);
    }
  }, [pinPosition?.lat, pinPosition?.lng, mapReady]);

  // ── Smooth pan when snappedPosition arrives from DB ──
  useEffect(() => {
    if (!mapReady || !snappedPosition) return;
    const { lat, lng } = snappedPosition;
    if (markerRef.current) {
      markerRef.current.setPosition({ lat, lng });
    }
    if (mapInstanceRef.current) {
      mapInstanceRef.current.panTo({ lat, lng });
    }
  }, [snappedPosition?.lat, snappedPosition?.lng, mapReady]);

  // ── Handle map click / marker drag → check google geocoding first, then find-by-coordinates API ──
  const handleMapInteraction = useCallback(async (lat: number, lng: number) => {
    setIsLoading(true);

    // Immediately update pin
    if (markerRef.current) {
      markerRef.current.setPosition({ lat, lng });
    }

    let foundShortAddress = "";
    let geocodedData: any = null;

    // 1. Check if Google Geocoder provides the short address
    try {
      const geocoder = new window.google.maps.Geocoder();
      const response = await geocoder.geocode({ location: { lat, lng } });

      if (response.results && response.results.length > 0) {
        geocodedData = response.results[0];
        
        // Search JSON string of geocoded results for Saudi short address
        const textToSearch = JSON.stringify(response.results);
        const match = textToSearch.match(/\b([a-zA-Z]{4}\d{4})\b/);
        if (match) {
          foundShortAddress = match[1].toUpperCase();
        }
      }
    } catch (err) {
      console.error("Google Geocoding failed:", err);
    }

    if (foundShortAddress && geocodedData) {
      const comp = geocodedData.address_components || [];
      const getComp = (type: string): string =>
        comp.find((c: any) => c.types.includes(type))?.long_name || "";

      const country = getComp("country");
      const city = getComp("locality") || getComp("administrative_area_level_2");
      const district = getComp("neighborhood") || getComp("sublocality") || getComp("administrative_area_level_3");
      const street = getComp("route");
      const building = getComp("street_number") || getComp("premise");
      const postalCode = getComp("postal_code");

      onLocationPickedRef.current({
        lat,
        lng,
        address: geocodedData.formatted_address || "",
        shortAddress: foundShortAddress,
        countryName: country,
        cityName: city,
        districtName: district,
        street,
        buildingNumber: building,
        postalCode,
        source: "google_geocode",
      });
      setIsLoading(false);
      return;
    }

    // 2. Check coordinates API
    try {
      const res = await fetch(`${API_BASE}/find-by-coordinates?lat=${lat}&lon=${lng}`);
      const apiData = await res.json();

      if (apiData?.success && apiData?.data) {
        const addr = apiData.data?.dataValues || apiData.data;
        const source: string = addr.source ?? "database";

        // If DB has precise coords, snap to them
        let finalLat = lat;
        let finalLng = lng;
        if (addr.latitude && addr.longitude) {
          finalLat = parseFloat(addr.latitude);
          finalLng = parseFloat(addr.longitude);

          if (markerRef.current) {
            markerRef.current.setPosition({ lat: finalLat, lng: finalLng });
          }
          if (mapInstanceRef.current) {
            mapInstanceRef.current.panTo({ lat: finalLat, lng: finalLng });
          }
        }

        onLocationPickedRef.current({
          lat: finalLat,
          lng: finalLng,
          address: `${addr.buildingNumber || ""} ${addr.street || ""} ${addr.district || ""} ${addr.city || ""}`.trim(),
          shortAddress: addr.name || "",
          countryName: "Saudi Arabia",
          cityName: addr.city || "",
          districtName: addr.district || "",
          street: addr.street || "",
          buildingNumber: addr.buildingNumber || "",
          postalCode: addr.postalCode || "",
          source,
        });

        setIsLoading(false);
        return;
      }
    } catch (err) {
      console.error("find-by-coordinates failed:", err);
    }

    // 3. Fallback: geocoded data but no short address found
    if (geocodedData) {
      const comp = geocodedData.address_components || [];
      const getComp = (type: string): string =>
        comp.find((c: any) => c.types.includes(type))?.long_name || "";

      const country = getComp("country");
      const city = getComp("locality") || getComp("administrative_area_level_2");
      const district = getComp("neighborhood") || getComp("sublocality") || getComp("administrative_area_level_3");
      const street = getComp("route");
      const building = getComp("street_number") || getComp("premise");
      const postalCode = getComp("postal_code");

      onLocationPickedRef.current({
        lat,
        lng,
        address: geocodedData.formatted_address || "",
        shortAddress: "",
        countryName: country,
        cityName: city,
        districtName: district,
        street,
        buildingNumber: building,
        postalCode,
        source: "google_geocode",
      });
    }

    setIsLoading(false);
  }, []);

  const handleUseMyLocation = useCallback(() => {
    if (navigator.geolocation) {
      setIsLoading(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          if (mapInstanceRef.current) {
            mapInstanceRef.current.setCenter({ lat, lng });
            mapInstanceRef.current.setZoom(16);
          }
          if (markerRef.current) {
            markerRef.current.setPosition({ lat, lng });
          }

          await handleMapInteraction(lat, lng);
        },
        () => {
          console.warn("User denied geolocation permission.");
          setIsLoading(false);
        }
      );
    }
  }, [handleMapInteraction]);

  useEffect(() => {
    const initMap = () => {
      if (!mapRef.current || !window.google) return;

      const parsedLat = initialLat ? parseFloat(initialLat.toString()) : NaN;
      const parsedLng = initialLng ? parseFloat(initialLng.toString()) : NaN;

      const hasCoords = !isNaN(parsedLat) && !isNaN(parsedLng);
      const centerLat = hasCoords ? parsedLat : 24.7136;
      const centerLng = hasCoords ? parsedLng : 46.6753;
      const initialZoom = hasCoords ? 16 : 6;

      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: centerLat, lng: centerLng },
        zoom: initialZoom,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });

      markerRef.current = new window.google.maps.Marker({
        position: { lat: centerLat, lng: centerLng },
        map: mapInstanceRef.current,
        draggable: true,
      });

      markerRef.current.addListener("dragend", () => {
        const position = markerRef.current.getPosition();
        if (position) {
          handleMapInteraction(position.lat(), position.lng());
        }
      });

      mapInstanceRef.current.addListener("click", (e: any) => {
        if (e.latLng) {
          markerRef.current.setPosition(e.latLng);
          handleMapInteraction(e.latLng.lat(), e.latLng.lng());
        }
      });

      setMapReady(true);
    };

    if (window.google?.maps) {
      initMap();
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>(
      "script[data-google-maps]"
    );
    if (existing) {
      existing.addEventListener("load", initMap);
      return () => existing.removeEventListener("load", initMap);
    }

    const script = document.createElement("script");
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY || "";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=en`;
    script.async = true;
    script.defer = true;
    (script as any).dataset.googleMaps = "true";
    script.addEventListener("load", initMap);
    document.body.appendChild(script);

    return () => {
      script.removeEventListener("load", initMap);
    };
  }, [initialLat, initialLng, handleMapInteraction]);

  return (
    <div style={{ position: "relative", borderRadius: 12, overflow: "hidden", border: "1px solid #e5e7eb" }}>
      <div className="h-64 w-full relative">
        <div ref={mapRef} className="absolute inset-0 w-full h-full" />
        {!mapReady && (
          <div className="absolute inset-0 bg-gray-50 flex items-center justify-center">
            <div className="text-center p-4">
              <FiMapPin className="mx-auto h-10 w-10 text-gray-400 animate-bounce" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                Loading map…
              </h3>
              <p className="mt-1 text-xs text-gray-500">
                Initializing Google Maps
              </p>
            </div>
          </div>
        )}

        {/* Loading overlay */}
        {isLoading && mapReady && (
          <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-10 bg-white/90 backdrop-blur-sm rounded-full px-4 py-1.5 shadow-md flex items-center gap-2 border border-gray-200">
            <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            <span className="text-xs text-gray-600 font-medium">Looking up address…</span>
          </div>
        )}

        {/* My Location button */}
        {mapReady && (
          <button
            type="button"
            onClick={handleUseMyLocation}
            disabled={isLoading}
            className="absolute bottom-3 right-3 z-10 inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-semibold rounded-full shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FBD037] disabled:opacity-50 transition-all cursor-pointer"
          >
            <FiCrosshair className="mr-1" />
            My Location
          </button>
        )}
      </div>

      {/* Bottom hint */}
      <div style={{
        position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)", zIndex: 10,
        background: "rgba(255,255,255,0.9)", color: "#475569", fontSize: 11, padding: "4px 12px",
        borderRadius: 20, pointerEvents: "none", backdropFilter: "blur(6px)", whiteSpace: "nowrap",
        border: "1px solid #e5e7eb"
      }}>
        Click or drag pin to auto-fill address
      </div>
    </div>
  );
};

export default memo(OutletGoogleMap);
